import { detectLaunchContext, type LaunchContext } from "@common-ui/utilities/launchContext";

import DeviceUtility from "@/helpers/device";
import { isMeFeatureFlagEnabled, type MeWithConfig } from "@/helpers/meFeatureFlags";
import StorageWrapper from "@/helpers/storage";

/**
 * LSP-22421 (parent LSP-22420): should the dashboard show the "the Home Screen web app is being retired,
 * get the LiveSwitch mobile app" dialog after login?
 *
 * Why it exists: on iOS a site added to the Home Screen runs in its own storage partition. A recording
 * started there that does not finish uploading sits in that partition's IndexedDB; the pending-upload
 * reminder email can only open in Safari, which cannot see it, so the recording looks lost. Phase 1 warns
 * and promotes the native app; Phase 2 (LSP-22422) blocks capture.
 *
 * The gate, every condition logged (CLAUDE.md "Diagnostic logging when fixing silent failures" — a login
 * dialog that silently does not appear is exactly the kind of gate that costs hours to triage):
 *
 *   1. `me` has loaded and carries the kill switch ON (`config.featureFlags.ios_home_screen_deprecation_warning`).
 *   2. The page is running as a Home Screen web app (`launchSurface === "home-screen"`, LSP-21256).
 *   3. The device is iOS or iPadOS — Android Chrome shares storage with its installed PWA, so Android is
 *      unaffected and must never see this.
 *   4. The dialog is not snoozed on this device.
 *
 * Snooze is per device in localStorage (StorageWrapper falls back to sessionStorage): the whole point is
 * that the Home Screen app is a distinct partition, so a device-local snooze is exactly the right scope.
 */

export const IOS_HOME_SCREEN_DEPRECATION_FLAG_KEY = "ios_home_screen_deprecation_warning" as const;

/** localStorage key holding the ISO timestamp until which the dialog stays hidden on this device. */
export const IOS_HOME_SCREEN_DEPRECATION_SNOOZE_STORAGE_KEY = "iosHomeScreenDeprecationSnoozedUntil";

/** "Not now": ask again tomorrow. */
export const IOS_HOME_SCREEN_DEPRECATION_NOT_NOW_SNOOZE_DAYS = 1;
/** "Get the app": they have gone to the store; give them a week before nagging again. */
export const IOS_HOME_SCREEN_DEPRECATION_GET_APP_SNOOZE_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;
const LOG_PREFIX = "[ios-home-screen-deprecation]";

export type IosHomeScreenDeprecationSuppressedReason = "me-not-loaded" | "flag-off" | "not-home-screen" | "not-ios" | "snoozed";

export type IosHomeScreenDeprecationDecision =
	| { show: true; launchContext: LaunchContext }
	| { show: false; reason: IosHomeScreenDeprecationSuppressedReason; launchContext: LaunchContext };

export interface IosHomeScreenDeprecationStorage {
	getItem(key: string): string | null;
	setItem(key: string, value: string): void;
	removeItem(key: string): void;
}

export interface IosHomeScreenDeprecationGateInput {
	me: MeWithConfig | null | undefined;
	launchContext?: LaunchContext;
	isIos?: boolean;
	storage?: IosHomeScreenDeprecationStorage;
	now?: Date;
}

/** Reads the snooze; a missing, unparseable or expired value means "not snoozed". Never throws. */
export function readIosHomeScreenDeprecationSnoozedUntil(
	storage: IosHomeScreenDeprecationStorage = StorageWrapper,
	now: Date = new Date()
): Date | null {
	try {
		const raw = storage.getItem(IOS_HOME_SCREEN_DEPRECATION_SNOOZE_STORAGE_KEY);
		if (!raw) return null;
		const until = new Date(raw);
		if (Number.isNaN(until.getTime())) {
			console.warn(`${LOG_PREFIX} ignoring an unparseable snooze value`, { raw });
			storage.removeItem(IOS_HOME_SCREEN_DEPRECATION_SNOOZE_STORAGE_KEY);
			return null;
		}
		return until.getTime() > now.getTime() ? until : null;
	} catch (error) {
		// Storage unavailable (private mode, quota): treat as not snoozed. The dialog may repeat; it never blocks login.
		console.warn(`${LOG_PREFIX} could not read the snooze; treating as not snoozed`, error);
		return null;
	}
}

/** Hides the dialog on this device for `days`. Never throws — a snooze that cannot be saved just means the dialog returns. */
export function snoozeIosHomeScreenDeprecationDialog(
	days: number,
	storage: IosHomeScreenDeprecationStorage = StorageWrapper,
	now: Date = new Date()
): Date | null {
	const until = new Date(now.getTime() + days * DAY_MS);
	try {
		storage.setItem(IOS_HOME_SCREEN_DEPRECATION_SNOOZE_STORAGE_KEY, until.toISOString());
		return until;
	} catch (error) {
		console.warn(`${LOG_PREFIX} could not save the snooze; the dialog will return on the next login`, error);
		return null;
	}
}

function defaultIsIos(): boolean {
	try {
		return DeviceUtility.isIosOrIpados;
	} catch (error) {
		console.warn(`${LOG_PREFIX} could not detect the platform; treating as not iOS`, error);
		return false;
	}
}

/**
 * The gate. Pure given its inputs; the defaults read the real window, device and storage. Every
 * suppressed outcome names its reason so a "why did nobody see the warning" question is one log search.
 */
export function decideIosHomeScreenDeprecationDialog(input: IosHomeScreenDeprecationGateInput): IosHomeScreenDeprecationDecision {
	const launchContext = input.launchContext ?? detectLaunchContext();
	const suppressed = (reason: IosHomeScreenDeprecationSuppressedReason): IosHomeScreenDeprecationDecision => {
		console.info(`${LOG_PREFIX} dialog suppressed`, { reason, launchContext });
		return { show: false, reason, launchContext };
	};

	if (!input.me) return suppressed("me-not-loaded");
	if (!isMeFeatureFlagEnabled(input.me, IOS_HOME_SCREEN_DEPRECATION_FLAG_KEY)) return suppressed("flag-off");
	if (launchContext.launchSurface !== "home-screen") return suppressed("not-home-screen");
	if (!(input.isIos ?? defaultIsIos())) return suppressed("not-ios");

	const now = input.now ?? new Date();
	const snoozedUntil = readIosHomeScreenDeprecationSnoozedUntil(input.storage ?? StorageWrapper, now);
	if (snoozedUntil) {
		console.info(`${LOG_PREFIX} dialog suppressed`, { reason: "snoozed", snoozedUntil: snoozedUntil.toISOString(), launchContext });
		return { show: false, reason: "snoozed", launchContext };
	}

	console.info(`${LOG_PREFIX} dialog will show`, { launchContext });
	return { show: true, launchContext };
}
